import { noop } from 'lodash';
import { createContext, PropsWithChildren, useCallback, useMemo } from 'react';

import { FormProvider, useForm } from '@/components/react-hook-form';
import { useGuardedContext } from '@/hooks/useGuardedContext';
import { useOnBeforeUnloadPrompt } from '@/hooks/useOnBeforeUnloadPrompt';
import { SlideEditorFormShape } from '@/modules/slideEditor/SlideEditor.types';

import { usePresentationTemplateEditorContext } from '../../PresentationTemplateEditor.context';
import { PresentationTemplateEditorStage } from '../../PresentationTemplateEditor.types';
import {
  mapPresentationTemplateBundleToSlideEditor,
  mapSlideEditorToPresentationTemplateBundle,
} from './PresentationTemplateSlideEditor.utils';

export interface SlideEditorBatchFormShape {
  bundles: (SlideEditorFormShape | null)[];
}

export interface PresentationTemplateSlideEditorContextShape {
  handleSaveAll: () => void;
  handleCancel: () => void;
  /**
   * Looks up the form array index for a given bundle ID.
   * Returns -1 if the bundle is not found.
   */
  getBundleIndexById: (bundleId: string) => number;
}

export const PresentationTemplateSlideEditorContext =
  createContext<PresentationTemplateSlideEditorContextShape>({
    handleSaveAll: noop,
    handleCancel: noop,
    getBundleIndexById: () => -1,
  });

export function usePresentationTemplateSlideEditorContext() {
  return useGuardedContext(
    PresentationTemplateSlideEditorContext,
    'PresentationTemplateSlideEditorContext'
  );
}

export function PresentationTemplateSlideEditorContextProvider({
  children,
}: PropsWithChildren) {
  const { bundlesFormArray, markBundlesDirty, setStage } =
    usePresentationTemplateEditorContext();
  const { fields: bundles } = bundlesFormArray;

  // Create a stable mapping of bundle IDs to their indices, computed once on mount.
  // This ensures that even if the parent context re-renders and provides a new
  // bundles array reference, we maintain stable index lookups for form field access.
  const bundleIdToIndexMap = useMemo<Map<string, number>>(() => {
    const map = new Map<string, number>();
    bundles.forEach((bundle, index) => {
      map.set(bundle.id, index);
    });
    return map;
    // Only compute on mount - bundles from parent shouldn't change while editing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getBundleIndexById = useCallback(
    (bundleId: string): number => {
      return bundleIdToIndexMap.get(bundleId) ?? -1;
    },
    [bundleIdToIndexMap]
  );

  const defaultValues = useMemo<SlideEditorBatchFormShape>(
    () => ({
      bundles: bundles.map(mapPresentationTemplateBundleToSlideEditor),
    }),
    // Only compute default values on mount - bundles from parent shouldn't change while editing
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const form = useForm<SlideEditorBatchFormShape>({
    defaultValues,
  });

  // Block browser unload when the slide editor's batch form has a committed
  // edit. This protects against closing or reloading the browser tab.
  useOnBeforeUnloadPrompt(form.formState.isDirty);

  const handleSaveAll = useCallback(() => {
    const formData = form.getValues();
    const slideEditorWasDirty = form.formState.isDirty;

    // Sync each edited bundle back to the parent form
    formData.bundles.forEach((slideEditorBundle, index) => {
      const originalBundle = bundles[index];
      if (!originalBundle) return;

      const updatedBundle = mapSlideEditorToPresentationTemplateBundle(
        slideEditorBundle,
        originalBundle
      );

      // only update if there's a valid bundle to update
      if (updatedBundle) {
        bundlesFormArray.update(index, updatedBundle);
      }
    });

    // useFieldArray.update() does not propagate dirty state to the parent
    // form, so the template editor's Save button would stay disabled even
    // after the user edits slide titles, content, or prompts. Flip the
    // parent form's isDirty when the slide editor had any in-flight edits.
    if (slideEditorWasDirty) {
      markBundlesDirty();
    }

    setStage(PresentationTemplateEditorStage.Template);
  }, [form, bundles, bundlesFormArray, markBundlesDirty, setStage]);

  // Exit discards the breakout form edits without a confirmation prompt.
  const handleCancel = useCallback(() => {
    setStage(PresentationTemplateEditorStage.Template);
  }, [setStage]);

  return (
    <PresentationTemplateSlideEditorContext.Provider
      value={{
        handleSaveAll,
        handleCancel,
        getBundleIndexById,
      }}
    >
      <FormProvider {...form}>{children}</FormProvider>
    </PresentationTemplateSlideEditorContext.Provider>
  );
}
